import type { DeviceIdentity } from '@lbc/shared';
import { z } from 'zod';

const STORAGE_KEY = 'lbc.device-identity.v1';
const KNOWN_GROUPS_KEY = 'lbc.known-groups.v2';
const INSTALLATION_KEY = 'lbc.installation.v3';

const identitySchema = z.object({
  groupId: z.uuid(),
  groupCode: z.string().regex(/^\d{6}$/),
  participantId: z.uuid(),
  sessionToken: z.string().length(64),
  role: z.enum(['admin', 'participant']),
});

const knownGroupSchema = z.object({
  identity: identitySchema,
  groupName: z.string().nullable(),
  lastUsedAt: z.string(),
});

const localMembershipSchema = z.object({
  id: z.uuid(),
  groupId: z.uuid(),
  participantId: z.uuid(),
  // The v2 session remains necessary while group bootstrap still uses v1 routes.
  legacyIdentity: identitySchema,
  groupName: z.string().nullable(),
  displayName: z.string().trim().min(1).max(30),
  joinedAt: z.iso.datetime({ offset: true }),
});

const localInstallationSchema = z
  .object({
    installationId: z.uuid(),
    installationToken: z.string().length(64),
    profile: z.object({
      weightKg: z.number().min(30).max(250).multipleOf(0.1),
      heightCm: z.number().min(120).max(230).multipleOf(0.1).nullable().optional(),
      notificationsEnabled: z.boolean().optional(),
    }),
    memberships: z.array(localMembershipSchema),
    lastSelectedGroupId: z.uuid().nullable(),
  })
  .superRefine((installation, context) => {
    const membershipIds = new Set<string>();
    const groupIds = new Set<string>();
    for (const membership of installation.memberships) {
      if (
        membership.legacyIdentity.groupId !== membership.groupId ||
        membership.legacyIdentity.participantId !== membership.participantId
      ) {
        context.addIssue({
          code: 'custom',
          message: 'Die Legacy-Sitzung muss zu der gespeicherten Mitgliedschaft passen.',
          path: ['memberships'],
        });
      }
      if (membershipIds.has(membership.id)) {
        context.addIssue({
          code: 'custom',
          message: 'Mitgliedschaft darf nur einmal gespeichert werden.',
          path: ['memberships'],
        });
      }
      membershipIds.add(membership.id);
      if (groupIds.has(membership.groupId)) {
        context.addIssue({
          code: 'custom',
          message: 'Pro Gruppe darf nur eine aktive Mitgliedschaft gespeichert werden.',
          path: ['memberships'],
        });
      }
      groupIds.add(membership.groupId);
    }
    if (
      installation.lastSelectedGroupId !== null &&
      !groupIds.has(installation.lastSelectedGroupId)
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Die ausgewählte Gruppe muss eine aktive Mitgliedschaft sein.',
        path: ['lastSelectedGroupId'],
      });
    }
  });

export type KnownGroup = z.infer<typeof knownGroupSchema>;
export type LocalMembership = z.infer<typeof localMembershipSchema>;
export type LocalInstallation = z.infer<typeof localInstallationSchema>;
export type LegacyInstallationMigration = Omit<LocalInstallation, 'lastSelectedGroupId'> & {
  lastSelectedGroupId?: string | null;
};

function readKnownGroups(): KnownGroup[] {
  const raw = window.localStorage.getItem(KNOWN_GROUPS_KEY);
  if (raw === null) {
    return [];
  }
  try {
    const parsed = z.array(knownGroupSchema).safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : [];
  } catch {
    return [];
  }
}

function persistKnownGroups(groups: KnownGroup[]): void {
  window.localStorage.setItem(KNOWN_GROUPS_KEY, JSON.stringify(groups));
}

function readLegacyIdentities(): KnownGroup[] {
  const groups = readKnownGroups();
  const current = identityStore.read();
  if (current !== null && !groups.some((group) => group.identity.groupId === current.groupId)) {
    return [
      { identity: current, groupName: null, lastUsedAt: new Date(0).toISOString() },
      ...groups,
    ];
  }
  return groups;
}

function readInstallation(): LocalInstallation | null {
  const raw = window.localStorage.getItem(INSTALLATION_KEY);
  if (raw === null) {
    return null;
  }
  try {
    const parsed = localInstallationSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

function legacySelectedGroupId(): string | null {
  return identityStore.read()?.groupId ?? readLegacyIdentities()[0]?.identity.groupId ?? null;
}

/**
 * Compatibility API for the pre-v3, group-bound identity. v3 code must use
 * installationStore instead. These keys are deliberately retained for rollback.
 */
export const identityStore = {
  read(): DeviceIdentity | null {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    try {
      const parsed = identitySchema.safeParse(JSON.parse(raw));
      return parsed.success ? parsed.data : null;
    } catch {
      return null;
    }
  },

  write(identity: DeviceIdentity, groupName: string | null = null): void {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(identity));
    const existing = readKnownGroups().filter(
      (group) => group.identity.groupId !== identity.groupId,
    );
    persistKnownGroups([
      { identity, groupName, lastUsedAt: new Date().toISOString() },
      ...existing,
    ]);
  },

  clear(): void {
    window.localStorage.removeItem(STORAGE_KEY);
  },

  readAll(): KnownGroup[] {
    return readLegacyIdentities();
  },

  activate(groupId: string): boolean {
    const group = this.readAll().find((entry) => entry.identity.groupId === groupId);
    if (group === undefined) {
      return false;
    }
    this.write(group.identity, group.groupName);
    return true;
  },

  remove(groupId: string): void {
    persistKnownGroups(readKnownGroups().filter((group) => group.identity.groupId !== groupId));
    if (this.read()?.groupId === groupId) {
      this.clear();
    }
  },
};

/**
 * The device-local v3 installation. It intentionally has no "remove group"
 * operation: ending a membership is a server-authoritative action.
 */
export const installationStore = {
  read(): LocalInstallation | null {
    return readInstallation();
  },

  /** Reads legacy credentials without changing them or creating a v3 installation. */
  readLegacy(): KnownGroup[] {
    return readLegacyIdentities();
  },

  /**
   * Creates v3 storage exactly once after the server has created an installation.
   * Existing valid v3 state wins so that retries cannot overwrite a newer selection.
   */
  migrateLegacy(migration: LegacyInstallationMigration): LocalInstallation | null {
    const existing = readInstallation();
    if (existing !== null) {
      return existing;
    }

    const selectedGroupId =
      migration.lastSelectedGroupId ??
      legacySelectedGroupId() ??
      migration.memberships[0]?.groupId ??
      null;
    const candidate = {
      ...migration,
      lastSelectedGroupId: selectedGroupId,
    };
    const parsed = localInstallationSchema.safeParse(candidate);
    if (!parsed.success) {
      return null;
    }
    window.localStorage.setItem(INSTALLATION_KEY, JSON.stringify(parsed.data));
    return parsed.data;
  },

  /** Stores server-confirmed installation data without touching any legacy key. */
  write(installation: LocalInstallation): boolean {
    const parsed = localInstallationSchema.safeParse(installation);
    if (!parsed.success) {
      return false;
    }
    window.localStorage.setItem(INSTALLATION_KEY, JSON.stringify(parsed.data));
    return true;
  },

  selectGroup(groupId: string): boolean {
    const installation = readInstallation();
    if (
      installation === null ||
      !installation.memberships.some((membership) => membership.groupId === groupId)
    ) {
      return false;
    }
    return this.write({ ...installation, lastSelectedGroupId: groupId });
  },

  /** Applies the server's active-membership snapshot without touching legacy storage. */
  synchronizeMemberships(memberships: LocalMembership[]): boolean {
    const installation = readInstallation();
    if (installation === null) {
      return false;
    }
    const selectedGroupId = memberships.some(
      (membership) => membership.groupId === installation.lastSelectedGroupId,
    )
      ? installation.lastSelectedGroupId
      : (memberships[0]?.groupId ?? null);
    return this.write({ ...installation, memberships, lastSelectedGroupId: selectedGroupId });
  },

  selectedMembership(): LocalMembership | null {
    const installation = readInstallation();
    if (installation === null) {
      return null;
    }
    return (
      installation.memberships.find(
        (membership) => membership.groupId === installation.lastSelectedGroupId,
      ) ??
      installation.memberships[0] ??
      null
    );
  },
};
